import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  FlaskConical, Lock, Sparkles, Search, Percent, BarChart3, ShieldQuestion, Gauge,
  CircleSlash, Clock, History as HistoryIcon, AlertTriangle, ChevronRight, CheckCircle2,
  RefreshCw, Sun, Sunrise, HandCoins, Infinity as InfinityIcon, Target,
  TrendingUp, FileText, Users, Heart,
} from 'lucide-react'
import { useHome } from '@/hooks/useHome'
import { useAuth } from '@/hooks/useAuth'
import {
  useJALabAllowance, useJALabFixtureSearch, useJALabHistory, useJALabAnalyze, useJALabReopen,
  useJALabFamilies, useJALabCompetitions, useJALabCompetitionFixtures, useJALabPreview, getJALabErrorMessage,
  type JALabFixtureSummary, type JALabHistoryItem, type JALabAnalysisResult, type JALabOpportunity,
  type JALabFamily, type JALabFamilyAvailability, type JALabCompetitionSummary, type JALabCoverage,
} from '@/hooks/useJALab'
import { TeamCrest } from '@/components/TeamCrest'
import { RegisterBetModal } from '@/components/RegisterBetModal'
import { PlayedBadge } from '@/components/PlayedBadge'
import { formatOdds, formatProb, cn, formatFixtureDateTime, formatFixtureTime, resolveDisplayTimeZone } from '@/lib/utils'
import { todayISO, addDays } from '@/lib/date'
import { getCountryFlag } from '@/lib/countryFlags'
import { formatBookmakerDisplay } from '@/lib/bookmakerUtils'

/** JA Lab v3 (22/09/2026) -- Universal universe + Premium7 carousel + human UX.
 *  Backend is authority: no whitelist filter, no mock data, no invented logos/odds.
 *  Premium7 = quick-access visual shortcuts, NOT a global filter. */

const MAX_FAMILIES = 3

/** Tamaño de página de Discovery (el backend acota a 200). */
const DISCOVERY_PAGE_SIZE = 50

/** Premium7 competition IDs for visual highlighting (not filtering). */
const PREMIUM7_NAMES = new Set([
  'Premier League', 'LaLiga', 'Serie A', 'Bundesliga', 'Ligue 1',
  'UEFA Champions League', 'UEFA Europa League',
])

interface FixtureContext {
  fixture_id: number
  home_team: string
  away_team: string
  home_logo: string | null
  away_logo: string | null
  competition_name: string | null
  competition_country: string | null
  competition_logo_url: string | null
  kickoff_utc: string
  coverage_status?: 'SUPPORTED' | 'LIMITED'
}

function fromSearch(f: JALabFixtureSummary): FixtureContext {
  return {
    fixture_id: f.fixture_id,
    home_team: f.home_team,
    away_team: f.away_team,
    home_logo: f.home_logo,
    away_logo: f.away_logo,
    competition_name: f.competition_name,
    competition_country: f.competition_country,
    competition_logo_url: f.competition_logo_url,
    kickoff_utc: f.kickoff_utc,
    coverage_status: f.coverage_status,
  }
}

function fromHistory(h: JALabHistoryItem): FixtureContext {
  return {
    fixture_id: h.fixture_id,
    home_team: h.home_team ?? '—',
    away_team: h.away_team ?? '—',
    home_logo: h.home_logo,
    away_logo: h.away_logo,
    competition_name: h.competition_name,
    competition_country: null,
    competition_logo_url: null,
    kickoff_utc: h.kickoff_utc,
  }
}

// ─── Competition Logo (with flag fallback) ─────────────────────────────────

function CompetitionLogo({
  competition,
  size = 32,
}: {
  competition: { name: string | null; country: string | null; logo_url?: string | null }
  size?: number
}) {
  if (competition.logo_url) {
    return (
      <img
        src={competition.logo_url}
        alt={competition.name ?? ''}
        className="rounded-lg object-contain shrink-0"
        style={{ width: size, height: size }}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
      />
    )
  }
  return (
    <div
      className="rounded-lg bg-panel-elevated border border-border flex items-center justify-center text-sm shrink-0"
      style={{ width: size, height: size }}
    >
      {getCountryFlag(competition.country)}
    </div>
  )
}

// ─── Home ──────────────────────────────────────────────────────────────────

function LabHome({
  locked, competitions, loadingCompetitions: _loadingCompetitions, onSelectCompetition, onStartSearch,
  onExploreCountries, onGoToHistory, allowance, availableFixtures, availableTotal, loadingAvailableFixtures,
  analyzedFixtureIds, onSelectFixture,
}: {
  locked: boolean
  competitions: JALabCompetitionSummary[]
  loadingCompetitions: boolean
  onSelectCompetition: (c: JALabCompetitionSummary) => void
  onStartSearch: () => void
  onExploreCountries?: () => void
  onGoToHistory?: () => void
  allowance?: { used: number; limit: number | null; unlimited: boolean }
  availableFixtures?: JALabFixtureSummary[]
  availableTotal?: number
  loadingAvailableFixtures?: boolean
  analyzedFixtureIds?: Set<number>
  onSelectFixture?: (f: JALabFixtureSummary) => void
}) {
  const premium7 = useMemo(
    () => competitions.filter((c) => PREMIUM7_NAMES.has(c.name)).slice(0, 6),
    [competitions],
  )

  return (
    <div className="space-y-6">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border border-exact/25 bg-gradient-to-b from-exact-soft/15 via-panel to-panel px-6 py-10 md:px-10 md:py-14">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-exact/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-2xl space-y-4">
          <span className="eyebrow text-exact inline-flex items-center gap-1.5">
            <FlaskConical className="h-3.5 w-3.5" /> John Anthony Lab
          </span>
          <h1 className="font-display text-2xl md:text-4xl font-bold text-text leading-tight">
            Analiza el partido que tú elijas
          </h1>
          <p className="text-sm md:text-base text-text-muted leading-relaxed max-w-xl">
            No dependas solo de la selección diaria. En JA Lab eliges cualquier partido con cobertura
            soportada, eliges qué quieres estudiar, y John Anthony te dice qué es lo más probable y
            sólido.
          </p>
          {locked && (
            <div className="pt-2">
              <Link to="/mas/suscripcion" className="btn-brand-app inline-flex text-xs font-bold">
                <Lock className="h-3.5 w-3.5" /> Descubrir BLACK
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Búsqueda global -- siempre visible, Sección 2A del encargo */}
      {!locked && (
        <button
          onClick={onStartSearch}
          className="w-full flex items-center gap-2.5 bg-panel border border-border rounded-xl py-3 px-4 text-left hover:border-brand/40 transition"
        >
          <Search className="h-4 w-4 text-text-dim shrink-0" />
          <span className="text-xs font-bold text-text-dim">Buscar equipo, liga o partido...</span>
        </button>
      )}

      {/* Sprint "JA Lab -- Cierre final discovery UX" (25/09/2026, Sección 1 del encargo):
          partidos que JA Lab REALMENTE puede analizar ahora, directo en la entrada -- MISMO
          endpoint/criterio de "analizable" que ya usan Buscador y Explorar (`useJALabFixtureSearch`
          sin query, misma ventana de HOY/misma exclusión de competiciones bloqueadas que ya
          aplica `search_fixtures` en el backend, ver `app/ja_lab/service.py`). Nunca un segundo
          criterio de disponibilidad. Ordenado por fecha/hora (ya lo hace el backend) y, como
          desempate, por competición. */}
      {!locked && (availableFixtures?.length || loadingAvailableFixtures) && (
        <section className="space-y-3">
          <p className="eyebrow px-1">Partidos disponibles para analizar</p>
          {loadingAvailableFixtures && <SkeletonListInline />}
          {!loadingAvailableFixtures && availableFixtures && availableFixtures.length > 0 && (
            <div className="card p-1.5 divide-y divide-border/30">
              {availableFixtures.map((f) => (
                <FixtureRow
                  key={f.fixture_id}
                  fixture={f}
                  analyzed={analyzedFixtureIds?.has(f.fixture_id) ?? false}
                  onSelect={() => onSelectFixture?.(f)}
                />
              ))}
            </div>
          )}
          {/* P0 (07/10/2026): sin recorte silencioso -- si hay más analizables que los
              mostrados, se avisa y se enlaza al buscador (paginado). */}
          {!loadingAvailableFixtures && availableTotal != null && availableFixtures && availableTotal > availableFixtures.length && (
            <button
              onClick={onStartSearch}
              className="w-full py-2 rounded-xl border border-border text-[10px] font-black uppercase tracking-wider text-text-dim hover:text-text"
            >
              Ver los {availableTotal} partidos en el buscador
            </button>
          )}
        </section>
      )}

      {/* Allowance */}
      {!locked && allowance && (
        <div className="flex items-center gap-3">
          {allowance.unlimited ? (
            <div className="flex items-center gap-2">
              <InfinityIcon className="h-4 w-4 text-exact" />
              <span className="text-xs font-bold text-exact">Análisis ilimitados</span>
            </div>
          ) : (
            <>
              <div className="flex gap-1.5">
                {allowance.limit != null && Array.from({ length: allowance.limit }).map((_, i) => (
                  <span
                    key={i}
                    className={cn('h-2 w-6 rounded-full', i < allowance.used ? 'bg-exact' : 'bg-panel-elevated border border-border')}
                  />
                ))}
              </div>
              <span className="text-xs font-bold text-text-muted figure">{allowance.used} de {allowance.limit} análisis utilizados hoy</span>
            </>
          )}
        </div>
      )}

      {/* Competencias destacadas -- grid compacto, accesos rápidos (nunca filtro global) */}
      {!locked && premium7.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <p className="eyebrow">Competencias destacadas</p>
            {onExploreCountries && (
              <button onClick={onExploreCountries} className="text-[10px] font-black uppercase tracking-wider text-exact">
                Ver todas
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
            {premium7.map((c) => (
              <button
                key={c.competition_id}
                onClick={() => onSelectCompetition(c)}
                className="card p-2.5 flex flex-col items-center gap-1.5 text-center hover:bg-panel-elevated/40 transition"
              >
                <CompetitionLogo competition={c} size={30} />
                <p className="text-[10px] font-bold text-text leading-tight line-clamp-2">{c.name}</p>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Explorar por país / Mis análisis -- dos formas principales de encontrar un partido */}
      {!locked && (
        <section className="grid grid-cols-2 gap-3">
          <button
            onClick={onExploreCountries}
            className="card p-4 flex flex-col items-start gap-2 text-left hover:bg-panel-elevated/40 transition"
          >
            <div className="w-9 h-9 rounded-lg bg-exact-soft/15 border border-exact/25 flex items-center justify-center">
              <Search className="h-4 w-4 text-exact" />
            </div>
            <div>
              <p className="text-xs font-bold text-text">Explorar por país</p>
              <p className="text-[10px] text-text-dim mt-0.5">Todas las ligas del mundo</p>
            </div>
          </button>
          <button
            onClick={onGoToHistory}
            className="card p-4 flex flex-col items-start gap-2 text-left hover:bg-panel-elevated/40 transition"
          >
            <div className="w-9 h-9 rounded-lg bg-panel-elevated border border-border flex items-center justify-center">
              <HistoryIcon className="h-4 w-4 text-text-dim" />
            </div>
            <div>
              <p className="text-xs font-bold text-text">Mis análisis</p>
              <p className="text-[10px] text-text-dim mt-0.5">Historial de partidos analizados</p>
            </div>
          </button>
        </section>
      )}

      {/* Locked: capabilities */}
      {locked && (
        <section className="space-y-4">
          <p className="eyebrow">Qué podrás hacer</p>
          <div className="hairline" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 pt-1">
            {[
              { icon: Percent, label: 'Probabilidades reales por mercado' },
              { icon: BarChart3, label: 'Familias de análisis según cobertura real del partido' },
              { icon: Gauge, label: 'Cuotas y Valor Esperado (EV)' },
              { icon: ShieldQuestion, label: 'Riesgo y calidad de la información' },
              { icon: Sparkles, label: 'Lo más probable y sólido, si existe' },
              { icon: CircleSlash, label: 'Conclusión honesta: "sin recomendación" cuando corresponda' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-start gap-3">
                <Icon className="h-4 w-4 text-exact shrink-0 mt-0.5" />
                <p className="text-sm text-text-muted leading-snug">{label}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Locked: CTA */}
      {locked && (
        <section className="card p-5 md:p-6 space-y-2.5 border-border/60">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-text-dim" />
            <p className="text-sm font-bold text-text">Disponible en el plan BLACK</p>
          </div>
          <p className="text-xs text-text-muted leading-relaxed">
            JA Lab es la capacidad de pedirle a John Anthony que analice cualquier partido que te interese, no solo la selección del día.
          </p>
        </section>
      )}
    </div>
  )
}

// ─── Fixture List Row ──────────────────────────────────────────────────────

function FixtureRow({ fixture, analyzed, onSelect }: { fixture: JALabFixtureSummary; analyzed: boolean; onSelect: () => void }) {
  return (
    <button
      onClick={onSelect}
      className="data-row w-full text-left hover:bg-panel-elevated/40 transition rounded-xl px-2 -mx-2"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex items-center -space-x-1.5 shrink-0">
          <TeamCrest src={fixture.home_logo} alt={fixture.home_team} size={22} />
          <TeamCrest src={fixture.away_logo} alt={fixture.away_team} size={22} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-text truncate">{fixture.home_team} vs {fixture.away_team}</p>
          <div className="flex items-center gap-1.5">
            <CompetitionLogo competition={{ name: fixture.competition_name, country: fixture.competition_country, logo_url: fixture.competition_logo_url }} size={12} />
            <p className="text-[10px] text-text-dim truncate">{fixture.competition_name || 'Competición'}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {analyzed && (
          <span className="text-[8px] font-black uppercase text-exact border border-exact/40 rounded-full px-1.5 py-0.5">Analizado</span>
        )}
        <span className="text-[10px] font-mono text-text-dim">
          {formatFixtureDateTime(fixture.kickoff_utc, { withWeekday: false })}
        </span>
        <ChevronRight className="h-3.5 w-3.5 text-text-dim" />
      </div>
    </button>
  )
}

function SkeletonListInline() {
  return (
    <div className="space-y-2">
      {[0, 1, 2].map((i) => <div key={i} className="card h-12 animate-pulse bg-panel-elevated/40" />)}
    </div>
  )
}

// ─── Explorar: País/Región → Competición → Partidos ────────────────────────

interface CountryGroup {
  label: string
  competitions: JALabCompetitionSummary[]
  totalFixturesToday: number
  hasPremium: boolean
}

function groupCompetitionsByCountry(competitions: JALabCompetitionSummary[]): CountryGroup[] {
  const byLabel = new Map<string, JALabCompetitionSummary[]>()
  for (const c of competitions) {
    const list = byLabel.get(c.group_label) ?? []
    list.push(c)
    byLabel.set(c.group_label, list)
  }
  const groups: CountryGroup[] = Array.from(byLabel.entries()).map(([label, list]) => ({
    label,
    competitions: list.sort((a, b) => b.n_fixtures_today - a.n_fixtures_today),
    totalFixturesToday: list.reduce((sum, c) => sum + c.n_fixtures_today, 0),
    hasPremium: list.some((c) => c.is_premium),
  }))
  // Europa (continentales) y países con Premium7 primero, luego por volumen real de partidos.
  return groups.sort((a, b) => {
    if (a.label === 'Europa' && b.label !== 'Europa') return -1
    if (b.label === 'Europa' && a.label !== 'Europa') return 1
    if (a.hasPremium !== b.hasPremium) return a.hasPremium ? -1 : 1
    return b.totalFixturesToday - a.totalFixturesToday
  })
}

function ExploreCountriesView({
  competitions, loading, onSelectCountry, onBack,
}: {
  competitions: JALabCompetitionSummary[]
  loading: boolean
  onSelectCountry: (label: string) => void
  onBack: () => void
}) {
  const groups = useMemo(() => groupCompetitionsByCountry(competitions), [competitions])

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-[10px] font-black uppercase tracking-wider text-text-dim flex items-center gap-1">
        ← Inicio
      </button>
      <div>
        <h2 className="font-display text-lg font-bold text-text">Selecciona un país</h2>
        <p className="text-xs text-text-muted mt-0.5">Elige un país para ver sus competiciones</p>
      </div>
      {loading && <SkeletonListInline />}
      {!loading && groups.length === 0 && (
        <div className="card p-4 text-center border-border/60">
          <p className="text-xs text-text-muted">No hay competiciones con partidos disponibles hoy.</p>
        </div>
      )}
      <div className="divide-y divide-border/30">
        {groups.map((g) => (
          <button
            key={g.label}
            onClick={() => onSelectCountry(g.label)}
            className="w-full flex items-center gap-3 py-3 text-left hover:bg-panel-elevated/40 transition rounded-xl px-2 -mx-2"
          >
            <div className="w-9 h-9 rounded-lg bg-panel-elevated border border-border flex items-center justify-center text-base shrink-0">
              {g.label === 'Europa' ? '🇪🇺' : getCountryFlag(g.label)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-text truncate">{g.label}</p>
              <p className="text-[10px] text-text-dim">
                {g.competitions.length} {g.competitions.length === 1 ? 'competición' : 'competiciones'}
              </p>
            </div>
            <ChevronRight className="h-3.5 w-3.5 text-text-dim shrink-0" />
          </button>
        ))}
      </div>
    </div>
  )
}

function ExploreCountryView({
  countryLabel, competitions, loading, onSelectCompetition, onBack,
}: {
  countryLabel: string
  competitions: JALabCompetitionSummary[]
  loading: boolean
  onSelectCompetition: (c: JALabCompetitionSummary) => void
  onBack: () => void
}) {
  const inCountry = useMemo(
    () => competitions.filter((c) => c.group_label === countryLabel).sort((a, b) => b.n_fixtures_today - a.n_fixtures_today),
    [competitions, countryLabel],
  )

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-[10px] font-black uppercase tracking-wider text-text-dim flex items-center gap-1">
        ← Países
      </button>
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-panel-elevated border border-border flex items-center justify-center text-base shrink-0">
          {countryLabel === 'Europa' ? '🇪🇺' : getCountryFlag(countryLabel)}
        </div>
        <div>
          <h2 className="font-display text-lg font-bold text-text">{countryLabel}</h2>
          <p className="text-[10px] text-text-dim">Elige una competición</p>
        </div>
      </div>
      {loading && <SkeletonListInline />}
      <div className="divide-y divide-border/30">
        {inCountry.map((c) => (
          <button
            key={c.competition_id}
            onClick={() => onSelectCompetition(c)}
            className="w-full flex items-center gap-3 py-3 text-left hover:bg-panel-elevated/40 transition rounded-xl px-2 -mx-2"
          >
            <CompetitionLogo competition={c} size={32} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-text truncate flex items-center gap-1.5">
                {c.name}
                {c.is_premium && <Sparkles className="h-3 w-3 text-exact shrink-0" />}
              </p>
              <p className="text-[10px] text-text-dim">
                {c.n_fixtures_today} {c.n_fixtures_today === 1 ? 'partido hoy' : 'partidos hoy'}
              </p>
            </div>
            <ChevronRight className="h-3.5 w-3.5 text-text-dim shrink-0" />
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Competition Fixtures ──────────────────────────────────────────────────

function CompetitionFixturesView({
  competition, fixtures, loading, analyzedFixtureIds, onSelect, onBack,
}: {
  competition: JALabCompetitionSummary
  fixtures: JALabFixtureSummary[]
  loading: boolean
  analyzedFixtureIds: Set<number>
  onSelect: (f: JALabFixtureSummary) => void
  onBack: () => void
}) {
  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-[10px] font-black uppercase tracking-wider text-text-dim flex items-center gap-1">
        ← Todas las competiciones
      </button>
      <div className="card p-3 flex items-center gap-3">
        <CompetitionLogo competition={competition} size={36} />
        <div className="min-w-0">
          <p className="text-sm font-bold text-text">{competition.name}</p>
          <p className="text-[10px] text-text-dim">
            {fixtures.length} {fixtures.length === 1 ? 'partido disponible' : 'partidos disponibles'}
          </p>
        </div>
      </div>
      {loading && <SkeletonListInline />}
      {!loading && fixtures.length === 0 && (
        <div className="card p-4 text-center space-y-1 border-border/60">
          <p className="text-xs text-text-muted">No hay partidos disponibles hoy para esta competición.</p>
        </div>
      )}
      <div className="divide-y divide-border/30">
        {fixtures.map((f) => (
          <FixtureRow key={f.fixture_id} fixture={f} analyzed={analyzedFixtureIds.has(f.fixture_id)} onSelect={() => onSelect(f)} />
        ))}
      </div>
    </div>
  )
}

// ─── Search View ───────────────────────────────────────────────────────────

type SearchFilter = 'todos' | 'hoy' | 'manana'

function SearchView({
  query, setQuery, filter, setFilter, countryFilter, setCountryFilter, competitionFilter,
  setCompetitionFilter, availableCountries, availableCompetitions, filteredFixtures, total, hasMore,
  onLoadMore, searching, analyzedFixtureIds, onSelect, onBack,
}: {
  query: string
  setQuery: (q: string) => void
  filter: SearchFilter
  setFilter: (f: SearchFilter) => void
  countryFilter: string
  setCountryFilter: (c: string) => void
  competitionFilter: number | null
  setCompetitionFilter: (c: number | null) => void
  availableCountries: string[]
  availableCompetitions: JALabCompetitionSummary[]
  filteredFixtures: JALabFixtureSummary[]
  total: number
  hasMore: boolean
  onLoadMore: () => void
  searching: boolean
  analyzedFixtureIds: Set<number>
  onSelect: (f: JALabFixtureSummary) => void
  onBack: () => void
}) {
  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-[10px] font-black uppercase tracking-wider text-text-dim">
        ← Volver al inicio
      </button>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-dim" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar equipo o competición..."
          className="w-full bg-panel border border-border rounded-xl py-2.5 pl-9 pr-3 text-xs font-bold text-text focus:outline-none focus:border-brand/50"
          autoFocus
        />
      </div>
      <div className="flex gap-1.5 bg-panel-elevated/40 border border-border p-1 rounded-xl w-fit">
        {(['todos', 'hoy', 'manana'] as SearchFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              'px-3 py-1 rounded-lg text-[9px] font-black uppercase transition',
              filter === f ? 'bg-brand text-black' : 'text-text-dim',
            )}
          >
            {f === 'todos' ? 'Todos' : f === 'hoy' ? 'Hoy' : 'Mañana'}
          </button>
        ))}
      </div>
      {/* Sección 2 del encargo: filtros de país/competición, combinables entre sí y con el
          texto libre y el día -- sobre el MISMO universo analizable global, nunca limitado
          a las 7 Premium7. */}
      <div className="grid grid-cols-2 gap-2">
        <select
          value={countryFilter}
          onChange={(e) => {
            setCountryFilter(e.target.value)
            setCompetitionFilter(null)
          }}
          className="w-full bg-panel border border-border rounded-xl py-2 px-2.5 text-[11px] font-bold text-text focus:outline-none focus:border-brand/50"
        >
          <option value="">Todos los países</option>
          {availableCountries.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={competitionFilter ?? ''}
          onChange={(e) => setCompetitionFilter(e.target.value ? Number(e.target.value) : null)}
          className="w-full bg-panel border border-border rounded-xl py-2 px-2.5 text-[11px] font-bold text-text focus:outline-none focus:border-brand/50"
        >
          <option value="">Todas las competiciones</option>
          {availableCompetitions.map((c) => (
            <option key={c.competition_id} value={c.competition_id}>{c.name}</option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <p className="eyebrow px-1">
          Partidos disponibles{total > 0 ? ` (${filteredFixtures.length} de ${total})` : ''}
        </p>
        {searching && <p className="text-xs text-text-dim px-1">Buscando...</p>}
        {!searching && filteredFixtures.length === 0 && (
          <p className="text-xs text-text-dim px-1">Sin partidos disponibles para analizar en este momento.</p>
        )}
        <div className="divide-y divide-border/30">
          {filteredFixtures.map((f) => (
            <FixtureRow
              key={f.fixture_id}
              fixture={f}
              analyzed={analyzedFixtureIds.has(f.fixture_id)}
              onSelect={() => onSelect(f)}
            />
          ))}
        </div>
        {hasMore && (
          <button
            onClick={onLoadMore}
            className="w-full mt-2 py-2 rounded-xl border border-border text-[10px] font-black uppercase tracking-wider text-text-dim hover:text-text"
          >
            Ver más ({total - filteredFixtures.length} restantes)
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Match Detail ──────────────────────────────────────────────────────────

type DetailTab = 'resumen' | 'alineaciones' | 'lesiones' | 'h2h' | 'forma'

const DETAIL_TABS: { key: DetailTab; label: string; icon: typeof FileText }[] = [
  { key: 'resumen', label: 'Resumen', icon: FileText },
  { key: 'alineaciones', label: 'Alineaciones', icon: Users },
  { key: 'lesiones', label: 'Lesiones', icon: Heart },
  { key: 'h2h', label: 'H2H', icon: Target },
  { key: 'forma', label: 'Forma', icon: TrendingUp },
]

function MatchDetailView({
  fixture, families, loadingFamilies, selectedFamilies, onToggleFamily, onAnalyze,
  alreadyAnalyzed, remaining, unlimited, onViewExisting, onBack,
}: {
  fixture: FixtureContext
  families: JALabFamilyAvailability[]
  loadingFamilies: boolean
  selectedFamilies: JALabFamily[]
  onToggleFamily: (f: JALabFamily) => void
  onAnalyze: () => void
  alreadyAnalyzed: boolean
  remaining: number | null
  unlimited: boolean
  onViewExisting: () => void
  onBack: () => void
}) {
  const [activeTab, setActiveTab] = useState<DetailTab>('resumen')
  const canAnalyze = unlimited || (remaining != null && remaining > 0)
  const { data: preview, isFetching: loadingPreview } = useJALabPreview(true, fixture.fixture_id)

  // Sección 5 del encargo ("disponibilidad de secciones"): la pestaña solo existe si tiene
  // una responsabilidad real -- Resumen siempre (el fixture ya califica como analizable,
  // Sección 1), el resto según la evidencia real que `get_match_preview` ya certificó
  // (`preview.sections`, mismo criterio que decide el CONTENIDO de cada pestaña, nunca un
  // segundo criterio de disponibilidad). Mientras carga, solo Resumen -- nunca cinco
  // pestañas provisionales que luego desaparecen.
  const visibleTabs = useMemo(() => {
    // Defensivo: una respuesta sin `sections` (caché vieja, forma inesperada) degrada a
    // solo Resumen en vez de romper la pantalla -- nunca se asume disponibilidad que no
    // fue certificada.
    if (!preview?.sections) return DETAIL_TABS.filter((t) => t.key === 'resumen')
    return DETAIL_TABS.filter((t) => {
      if (t.key === 'resumen') return true
      if (t.key === 'forma') return preview.sections.forma?.available === true
      if (t.key === 'h2h') return preview.sections.h2h?.available === true
      if (t.key === 'lesiones') return preview.sections.injuries?.status === 'available'
      if (t.key === 'alineaciones') return preview.sections.lineups?.status !== 'not_available'
      return true
    })
  }, [preview])

  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some((t) => t.key === activeTab)) {
      setActiveTab(visibleTabs[0].key)
    }
  }, [visibleTabs, activeTab])

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-[10px] font-black uppercase tracking-wider text-text-dim">
        ← Volver
      </button>

      {/* Team Header */}
      <div className="card p-5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
            <TeamCrest src={fixture.home_logo} alt={fixture.home_team} size={56} />
            <p className="text-sm font-bold text-text text-center truncate w-full">{fixture.home_team}</p>
            <span className="text-[9px] font-black uppercase tracking-wider text-text-dim">Local</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <span className="text-lg font-black text-text-dim">VS</span>
          </div>
          <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
            <TeamCrest src={fixture.away_logo} alt={fixture.away_team} size={56} />
            <p className="text-sm font-bold text-text text-center truncate w-full">{fixture.away_team}</p>
            <span className="text-[9px] font-black uppercase tracking-wider text-text-dim">Visitante</span>
          </div>
        </div>
        <div className="hairline pt-3 flex items-center justify-between text-[10px] text-text-dim">
          <span className="truncate flex items-center gap-1.5">
            <CompetitionLogo competition={{ name: fixture.competition_name, country: fixture.competition_country, logo_url: fixture.competition_logo_url }} size={14} />
            {fixture.competition_name || 'Competición'}
          </span>
          <span className="figure shrink-0">
            {formatFixtureDateTime(fixture.kickoff_utc, { withWeekday: false })}
          </span>
        </div>
      </div>

      {/* Coverage */}
      {fixture.coverage_status && (
        <div className={cn(
          'card p-3 flex items-center gap-2',
          fixture.coverage_status === 'SUPPORTED' ? 'border-success/30 bg-success/5' : 'border-warn/30 bg-warn/5',
        )}>
          <span className={cn(
            'text-[9px] font-black uppercase tracking-wider border rounded-full px-2 py-0.5',
            fixture.coverage_status === 'SUPPORTED'
              ? 'text-success border-success/40'
              : 'text-warn border-warn/40',
          )}>
            {fixture.coverage_status === 'SUPPORTED' ? 'Cobertura soportada' : 'Cobertura limitada'}
          </span>
          <span className="text-[10px] text-text-dim">
            {fixture.coverage_status === 'SUPPORTED'
              ? 'Información suficiente para análisis confiable'
              : 'Análisis disponible con información parcial'}
          </span>
        </div>
      )}

      {/* Tabs -- solo las que tienen una responsabilidad real para este fixture
          (Sección 5 del encargo: "no quiero cinco pestañas obligatorias si solo dos
          tienen datos") */}
      <div className="flex gap-1.5 bg-panel-elevated/40 border border-border p-1 rounded-xl overflow-x-auto no-scrollbar">
        {visibleTabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={cn(
              'flex-1 min-w-0 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition shrink-0',
              activeTab === key ? 'bg-exact text-black' : 'text-text-dim hover:text-text',
            )}
          >
            <Icon className="h-3 w-3 shrink-0" />
            <span className="truncate">{label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="card p-4 min-h-[120px]">
        {loadingPreview && (
          <div className="space-y-2 animate-pulse">
            <div className="h-3 bg-panel-elevated/60 rounded w-full" />
            <div className="h-3 bg-panel-elevated/60 rounded w-4/5" />
          </div>
        )}
        {!loadingPreview && activeTab === 'resumen' && (
          <div className="space-y-2">
            <p className="text-xs text-text-muted leading-relaxed">
              {preview?.resumen || 'No hay suficiente información real todavía para resumir este partido.'}
            </p>
          </div>
        )}
        {/* ALINEACIONES (02/10/2026, PRIORITARIO del encargo): cuando el proveedor ya
            publicó la alineación oficial (`Lineup.raw_json`, extraído de verdad por
            `analysis_engine._real_lineups`), se muestra el XI/banca/formación real --
            nunca se inventa un jugador que el proveedor no trajo. */}
        {!loadingPreview && activeTab === 'alineaciones' && (
          <div className="space-y-3">
            {preview?.sections?.lineups?.available && preview.lineups.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[...preview.lineups].sort((a, b) => (b.is_home ? 1 : 0) - (a.is_home ? 1 : 0)).map((l, i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-wider text-text">
                        {l.is_home ? fixture.home_team : fixture.away_team}
                      </span>
                      {l.formation && (
                        <span className="text-[9px] font-bold text-text-dim">{l.formation}</span>
                      )}
                    </div>
                    <ul className="space-y-1">
                      {l.starters.map((p, j) => (
                        <li key={j} className="text-xs text-text-muted flex items-center gap-2">
                          <span className="text-text-dim w-5 shrink-0 text-right">{p.number ?? ''}</span>
                          <span className="text-text font-bold truncate">{p.name}</span>
                          {p.position && <span className="text-text-dim text-[10px] shrink-0">{p.position}</span>}
                        </li>
                      ))}
                    </ul>
                    {l.substitutes.length > 0 && (
                      <>
                        <p className="text-[9px] font-black uppercase tracking-wider text-text-dim pt-1">Banca</p>
                        <ul className="space-y-1">
                          {l.substitutes.map((p, j) => (
                            <li key={j} className="text-[11px] text-text-dim flex items-center gap-2">
                              <span className="w-5 shrink-0 text-right">{p.number ?? ''}</span>
                              <span className="truncate">{p.name}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : preview?.sections?.lineups?.status === 'pending' ? (
              <p className="text-xs text-text-muted">
                Alineación oficial todavía no publicada por el proveedor -- se espera antes del partido.
              </p>
            ) : (
              <p className="text-xs text-text-muted">Alineación no disponible para este partido.</p>
            )}
          </div>
        )}
        {/* LESIONES/DISPONIBILIDAD: la pestaña solo existe cuando `sections.injuries.status
            === 'available'` (ver `visibleTabs`) -- aquí diferenciamos lista real de
            jugadores vs. "evidencia real de 0 reportados", nunca "ausencia de dato =
            sin lesionados" (si el dato fuera realmente desconocido, la pestaña ni se
            muestra). */}
        {!loadingPreview && activeTab === 'lesiones' && (
          <div className="space-y-2">
            {preview && preview.injuries.length > 0 ? (
              <ul className="space-y-1.5">
                {preview.injuries.map((inj, i) => (
                  <li key={i} className="text-xs text-text-muted flex items-center justify-between gap-2">
                    <span className="font-bold text-text">{inj.player}</span>
                    <span className="text-text-dim">{inj.reason || inj.status}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-text-muted">Sin jugadores lesionados ni sancionados reportados para este partido.</p>
            )}
          </div>
        )}
        {!loadingPreview && activeTab === 'h2h' && (
          <div className="space-y-2">
            {preview && preview.h2h.length > 0 ? (
              <ul className="divide-y divide-border/30">
                {preview.h2h.map((h, i) => (
                  <li key={i} className="py-2 space-y-0.5">
                    <p className="text-xs font-bold text-text">{h.home} {h.score} {h.away}</p>
                    <p className="text-[10px] text-text-dim">
                      {h.competition || 'Competición'}{h.date ? ` · ${h.date}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-text-muted">No encontramos enfrentamientos recientes entre estos equipos.</p>
            )}
          </div>
        )}
        {!loadingPreview && activeTab === 'forma' && (
          <div className="space-y-2">
            <p className="text-xs text-text-muted leading-relaxed">{preview?.forma.home || 'Sin datos recientes disponibles.'}</p>
            <p className="text-xs text-text-muted leading-relaxed">{preview?.forma.away || 'Sin datos recientes disponibles.'}</p>
          </div>
        )}
      </div>

      {/* Family Selector */}
      {!loadingFamilies && families.length > 0 && (
        <div className="space-y-3">
          <div className="space-y-1">
            <p className="eyebrow px-1">¿Qué quieres analizar?</p>
            <p className="text-[10px] text-text-dim px-1">Elige entre 1 y {MAX_FAMILIES} familias — {selectedFamilies.length}/{MAX_FAMILIES} seleccionadas.</p>
          </div>
          <div className="space-y-1.5">
            {families.map((f) => {
              const isSelected = selectedFamilies.includes(f.family)
              const disabled = !f.available || (!isSelected && selectedFamilies.length >= MAX_FAMILIES)
              return (
                <button
                  key={f.family}
                  disabled={disabled && !isSelected}
                  onClick={() => onToggleFamily(f.family)}
                  className={cn(
                    'w-full flex items-center gap-3 p-3 rounded-xl border transition',
                    isSelected && 'border-brand/50 bg-brand-soft/10',
                    !f.available && 'opacity-40 cursor-not-allowed border-border/30',
                    disabled && !isSelected && f.available && 'opacity-50 cursor-not-allowed border-border/30',
                    !isSelected && f.available && 'border-border hover:border-text-dim/40',
                  )}
                >
                  <div className={cn(
                    'w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition',
                    isSelected ? 'bg-brand border-brand' : 'border-text-dim/40',
                  )}>
                    {isSelected && <CheckCircle2 className="h-3.5 w-3.5 text-black" />}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-xs font-bold text-text">{f.label}</p>
                    {!f.available && <p className="text-[9px] text-text-dim">No disponible para esta competición</p>}
                  </div>
                  {f.available && (
                    <span className="text-[9px] font-black uppercase tracking-wider text-success">Disponible</span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="space-y-2">
        {alreadyAnalyzed && (
          <button onClick={onViewExisting} className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-panel-elevated border border-exact/40 text-exact text-xs font-bold transition hover:bg-exact/10">
            <RefreshCw className="h-3.5 w-3.5" /> VER ÚLTIMO ANÁLISIS
          </button>
        )}
        <button
          onClick={onAnalyze}
          disabled={selectedFamilies.length === 0 || (!canAnalyze && !alreadyAnalyzed)}
          className="btn-brand-app w-full text-xs font-bold justify-center disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Sparkles className="h-3.5 w-3.5" /> ANALIZAR CON JOHN ANTHONY
        </button>
        {!canAnalyze && !alreadyAnalyzed && (
          <p className="text-[10px] text-text-dim text-center">Ya usaste tus análisis de hoy. Nuevos análisis disponibles mañana.</p>
        )}
      </div>
    </div>
  )
}

// ─── Loading ───────────────────────────────────────────────────────────────

function LoadingView({ fixture }: { fixture: FixtureContext }) {
  return (
    <div className="space-y-4">
      <div className="card p-5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
            <TeamCrest src={fixture.home_logo} alt={fixture.home_team} size={48} />
            <p className="text-xs font-bold text-text text-center truncate w-full">{fixture.home_team}</p>
          </div>
          <span className="text-sm font-black text-text-dim">VS</span>
          <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
            <TeamCrest src={fixture.away_logo} alt={fixture.away_team} size={48} />
            <p className="text-xs font-bold text-text text-center truncate w-full">{fixture.away_team}</p>
          </div>
        </div>
      </div>
      <div className="card p-6 text-center space-y-3">
        <Sparkles className="h-6 w-6 mx-auto text-exact animate-pulse" />
        <p className="text-sm font-bold text-text">John Anthony está evaluando los mercados disponibles y comparando sus probabilidades con las cuotas actuales…</p>
      </div>
    </div>
  )
}

// ─── Time Awareness ────────────────────────────────────────────────────────

function TimeAwarenessBadge({ awareness, reason }: { awareness: 'anticipated' | 'updated' | null; reason: string | null }) {
  if (!awareness) return null
  const isUpdated = awareness === 'updated'
  const Icon = isUpdated ? Sun : Sunrise
  return (
    <div className={cn(
      'flex items-center gap-2 rounded-xl border px-3 py-2 text-[10px] font-bold',
      isUpdated ? 'border-success/30 bg-success/5 text-success' : 'border-warn/30 bg-warn/5 text-warn',
    )}>
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <div>
        <p className="uppercase tracking-wider">{isUpdated ? 'Contexto actualizado' : 'Análisis anticipado'}</p>
        {reason && <p className="text-text-muted font-medium normal-case mt-0.5">{reason}</p>}
      </div>
    </div>
  )
}

// ─── Coverage ──────────────────────────────────────────────────────────────

const COVERAGE_LEVEL_LABEL: Record<string, string> = {
  alta: 'Alta', media: 'Media', baja: 'Baja', no_disponible: 'No disponible',
}
const COVERAGE_LEVEL_CLASS: Record<string, string> = {
  alta: 'text-success border-success/40 bg-success/5',
  media: 'text-warn border-warn/40 bg-warn/5',
  baja: 'text-danger border-danger/40 bg-danger/5',
  no_disponible: 'text-text-dim border-border bg-panel-elevated/30',
}

function CoverageSection({ coverage, whatCouldChange }: { coverage: JALabCoverage | null; whatCouldChange: string[] }) {
  if (!coverage) return null
  return (
    <div className="card p-4 space-y-3">
      <p className="eyebrow">Cobertura de información de este partido</p>
      <div className="flex items-center gap-2">
        <span className={cn('text-[9px] font-black uppercase tracking-wider border rounded-full px-2 py-0.5', COVERAGE_LEVEL_CLASS[coverage.match.level])}>
          {COVERAGE_LEVEL_LABEL[coverage.match.level]}
        </span>
        <span className="text-[10px] text-text-dim">Forma, estadísticas recientes, H2H, alineaciones y lesiones conocidas</span>
      </div>
      <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
        {coverage.match.signals.map((s, i) => (
          <li key={i} className="flex items-center gap-1.5 text-text-muted">
            {s.status === 'pending' ? (
              <Clock className="h-3 w-3 text-warn shrink-0" />
            ) : s.available ? (
              <CheckCircle2 className="h-3 w-3 text-success shrink-0" />
            ) : (
              <CircleSlash className="h-3 w-3 text-text-dim shrink-0" />
            )}
            {s.label}
          </li>
        ))}
      </ul>
      <div className="hairline pt-2 space-y-1.5">
        <p className="text-[9px] font-black uppercase tracking-wider text-text-dim">Cobertura por tipo de análisis</p>
        <div className="grid grid-cols-2 gap-1.5">
          {coverage.families.map((f) => (
            <div key={f.family} className="flex flex-col gap-0.5 rounded-lg border border-border/40 px-2 py-1.5">
              <span className="text-[10px] text-text font-bold leading-tight">{f.label}</span>
              <span className={cn('text-[8px] font-black uppercase leading-tight', COVERAGE_LEVEL_CLASS[f.level].split(' ')[0])}>
                {COVERAGE_LEVEL_LABEL[f.level]}
              </span>
            </div>
          ))}
        </div>
      </div>
      {whatCouldChange.length > 0 && (
        <div className="hairline pt-2 space-y-1">
          <p className="text-[9px] font-black uppercase tracking-wider text-text-dim">Qué podría cambiar esta conclusión</p>
          <ul className="text-[10px] text-text-muted space-y-0.5">
            {whatCouldChange.map((note, i) => <li key={i}>· {note}</li>)}
          </ul>
        </div>
      )}
    </div>
  )
}

// ─── Opportunity Card (Redesigned with odds truth hierarchy) ───────────────

function OpportunityCard({
  opp, highlight, usageId, matchLabel,
}: { opp: JALabOpportunity; highlight?: boolean; usageId: number; matchLabel: string }) {
  const factorsFavor = opp.factors_favor ?? []
  const riskReasons = opp.risk_reasons ?? []
  const marketLabel = opp.market_label ?? opp.market
  const selectionLabel = opp.selection_label ?? opp.selection
  const oddsSource = opp.odds_source
  const bookmakerName = oddsSource?.bookmaker ?? null
  const referenceText = oddsSource?.reference_text ?? 'Fuente no disponible'
  const fetchedAt = oddsSource?.fetched_at
  const [showPlayModal, setShowPlayModal] = useState(false)

  return (
    <div className={cn('card p-4 space-y-3', highlight && 'border-brand/40 bg-brand-soft/10')}>
      {/* SELECCIÓN */}
      <div className="flex items-start justify-between gap-2">
        <div>
          {opp.family_label && (
            <span className="text-[8px] font-black uppercase tracking-wider text-brand">{opp.family_label}</span>
          )}
          <p className="text-sm font-bold text-text">{selectionLabel}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <p className="text-[10px] text-text-dim">{marketLabel}</p>
            {opp.side_label && (
              <span className="text-[8px] font-black uppercase text-text-dim border border-border rounded-full px-1.5">{opp.side_label}</span>
            )}
          </div>
        </div>
        {opp.probability != null && (
          <span className="figure text-xs font-black shrink-0 text-text">
            {formatProb(opp.probability)}
          </span>
        )}
      </div>

      {/* Métricas: PROBABILIDAD → CUOTA JUSTA → CUOTA MERCADO */}
      <div className="grid grid-cols-3 gap-2 text-center text-[10px]">
        <div>
          <p className="text-text-dim font-bold uppercase">Probabilidad</p>
          <p className="figure text-text font-bold">{formatProb(opp.probability)}</p>
        </div>
        <div>
          <p className="text-text-dim font-bold uppercase">Cuota justa JA</p>
          <p className="figure text-text font-bold">{formatOdds(opp.fair_odds)}</p>
        </div>
        <div>
          <p className="text-text-dim font-bold uppercase">Cuota de mercado</p>
          <p className="figure text-text font-bold">{formatOdds(opp.odds)}</p>
        </div>
      </div>

      {/* BOOKMAKER + CAPTURADA */}
      {(bookmakerName || referenceText) && (
        <div className="flex items-center justify-between gap-2 text-[10px]">
          {bookmakerName && (
            <span className="flex items-center gap-1.5 font-bold text-text">
              <span className="w-1.5 h-1.5 rounded-full bg-exact shrink-0" />
              {formatBookmakerDisplay(bookmakerName)}
            </span>
          )}
          {!bookmakerName && (
            <span className="text-text-dim">{referenceText}</span>
          )}
          {fetchedAt && (
            <span className="text-text-dim">
              {(() => {
                try {
                  // P1.2 (01/10/2026, Timezone Global): "mismo día" se compara en la zona de
                  // VISUALIZACIÓN real del usuario (resolveDisplayTimeZone()), nunca con
                  // Date.toDateString() -- ese usa la zona implícita del motor JS, que
                  // coincide casi siempre pero no cuando el usuario fijó una preferencia
                  // explícita distinta de la de su dispositivo.
                  const dayFmt = new Intl.DateTimeFormat('es-ES', { timeZone: resolveDisplayTimeZone(), year: 'numeric', month: '2-digit', day: '2-digit' })
                  if (dayFmt.format(new Date(fetchedAt)) === dayFmt.format(new Date())) {
                    return `Capturada hoy · ${formatFixtureTime(fetchedAt)}`
                  }
                  return formatFixtureDateTime(fetchedAt, { withWeekday: false })
                } catch { return referenceText }
              })()}
            </span>
          )}
        </div>
      )}

      {/* POR QUÉ JA LA RECOMIENDA */}
      {opp.why_text && (
        <div className="pt-2 border-t border-border/30 space-y-1">
          <p className="text-[9px] font-black uppercase tracking-wider text-text-dim">Por qué JA la recomienda</p>
          <p className="text-[11px] text-text-muted leading-relaxed">{opp.why_text}</p>
        </div>
      )}

      {/* Factores a favor */}
      {factorsFavor.length > 0 && (
        <div className="space-y-1">
          <p className="text-[9px] font-black uppercase tracking-wider text-text-dim">Datos que lo respaldan</p>
          <ul className="text-[11px] text-text-muted space-y-0.5">
            {factorsFavor.slice(0, 3).map((f, i) => <li key={i}>· {f}</li>)}
          </ul>
        </div>
      )}

      {/* Riesgos */}
      {riskReasons.length > 0 && (
        <div className="space-y-1">
          <p className="text-[9px] font-black uppercase tracking-wider text-text-dim">Qué debes tener en cuenta</p>
          <ul className="text-[11px] text-text-muted space-y-0.5">
            {riskReasons.slice(0, 3).map((e, i) => <li key={i}>· {e}</li>)}
          </ul>
        </div>
      )}

      {/* Consenso */}
      {opp.consensus_score != null && (
        <p className="text-[10px] text-text-dim">
          {opp.consensus_score >= 0.85
            ? 'Los modelos de John Anthony coinciden en esta lectura.'
            : 'Los modelos de John Anthony no coinciden del todo en esta lectura -- se muestra igual, con esa advertencia.'}
        </p>
      )}

      {/* Quality */}
      <div className="pt-1 flex items-center justify-between text-[9px] text-text-dim/80">
        <span>Calidad: {Math.round(opp.data_quality_score)}/100</span>
      </div>

      {/* La jugué */}
      {opp.my_bet ? (
        <PlayedBadge bet={opp.my_bet} />
      ) : (
        <button
          onClick={() => setShowPlayModal(true)}
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-panel-elevated border border-brand/40 text-brand hover:bg-brand/10 text-[10px] font-black uppercase tracking-wider transition"
        >
          <HandCoins className="h-3.5 w-3.5" /> La jugué
        </button>
      )}
      {showPlayModal && (
        <RegisterBetModal
          onClose={() => setShowPlayModal(false)}
          matchLabel={matchLabel}
          selectionLabel={`${marketLabel}: ${selectionLabel}`}
          referenceOdds={opp.odds ?? null}
          jaLabUsageId={usageId}
          market={opp.market}
        />
      )}
    </div>
  )
}

// ─── All Markets (RECOMENDADO vs ANALIZADO) ───────────────────────────────

function AllMarketsView({
  fixture, result, onBack,
}: {
  fixture: FixtureContext
  result: JALabAnalysisResult
  onBack: () => void
}) {
  const recommendations = result.recommendations ?? []

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-[10px] font-black uppercase tracking-wider text-text-dim">
        ← Volver al resultado
      </button>
      <div className="card p-3 flex items-center gap-3">
        <div className="flex items-center -space-x-1.5 shrink-0">
          <TeamCrest src={fixture.home_logo} alt={fixture.home_team} size={22} />
          <TeamCrest src={fixture.away_logo} alt={fixture.away_team} size={22} />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-bold text-text truncate">{fixture.home_team} vs {fixture.away_team}</p>
          <p className="text-[10px] text-text-dim">Todos los mercados evaluados</p>
        </div>
      </div>

      {/* Recomendados */}
      {recommendations.length > 0 && (
        <div className="space-y-2">
          <p className="eyebrow px-1 text-brand">Recomendados</p>
          <div className="space-y-1.5">
            {recommendations.map((opp, i) => (
              <div key={i} className="card p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold text-text truncate">{opp.selection_label}</p>
                  <p className="text-[9px] text-text-dim truncate">{opp.market_label}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[9px] font-black uppercase text-brand border border-brand/40 rounded-full px-1.5 py-0.5">
                    Recomendado
                  </span>
                  <span className="figure text-[10px] font-bold text-text">{formatProb(opp.probability)}</span>
                  <span className="figure text-[10px] text-text-dim">{formatOdds(opp.odds)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary: evaluated vs excluded */}
      <div className="card p-4 text-center space-y-1 border-border/60">
        <p className="text-[10px] text-text-dim">
          {result.markets_analyzed_count} mercados evaluados · {result.markets_excluded_count} sin información suficiente
        </p>
        <p className="text-[9px] text-text-dim/70">
          Un mercado analizado no es automáticamente una recomendación.
        </p>
      </div>
    </div>
  )
}

// ─── Analysis Result ───────────────────────────────────────────────────────

function AnalysisResultView({
  result, fixture, onReanalyze, onBack, onViewAllMarkets,
}: {
  result: JALabAnalysisResult
  fixture: FixtureContext
  onReanalyze: () => void
  onBack: () => void
  onViewAllMarkets: () => void
}) {
  if (result.status === 'insufficient_data' || result.status === 'analysis_failed') {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="text-[10px] font-black uppercase tracking-wider text-text-dim">
          ← Volver
        </button>
        <div className="card p-5 text-center space-y-2 border-warn/30">
          <AlertTriangle className="h-6 w-6 mx-auto text-warn" />
          <p className="text-sm font-bold text-text">No se pudo completar el análisis</p>
          <p className="text-xs text-text-muted">{result.failure_reason}</p>
        </div>
      </div>
    )
  }

  if (result.status === 'odds_unavailable') {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="text-[10px] font-black uppercase tracking-wider text-text-dim">
          ← Volver
        </button>
        <div className="card p-5 text-center space-y-2 border-warn/30">
          <AlertTriangle className="h-6 w-6 mx-auto text-warn" />
          <p className="text-sm font-bold text-text">Cuotas no disponibles</p>
          <p className="text-xs text-text-muted max-w-sm mx-auto">
            No pudimos completar el análisis de valor porque no hay cuotas suficientemente fiables en este momento.
          </p>
        </div>
      </div>
    )
  }

  const recommendations = result.recommendations ?? []
  const dataNotes = result.data_notes ?? []

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-[10px] font-black uppercase tracking-wider text-text-dim">
        ← Volver al partido
      </button>

      <div className="card p-5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
            <TeamCrest src={fixture.home_logo} alt={fixture.home_team} size={48} />
            <p className="text-xs font-bold text-text text-center truncate w-full">{fixture.home_team}</p>
          </div>
          <span className="text-sm font-black text-text-dim">VS</span>
          <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
            <TeamCrest src={fixture.away_logo} alt={fixture.away_team} size={48} />
            <p className="text-xs font-bold text-text text-center truncate w-full">{fixture.away_team}</p>
          </div>
        </div>
        <div className="hairline pt-2 flex items-center justify-between text-[10px] text-text-dim">
          <span className="truncate flex items-center gap-1.5">
            <CompetitionLogo competition={{ name: fixture.competition_name, country: fixture.competition_country, logo_url: fixture.competition_logo_url }} size={12} />
            {fixture.competition_name}
          </span>
          <span className="shrink-0">
            {result.is_reanalysis ? `Reanálisis · v${result.version}` : 'Analizado'}
          </span>
        </div>
      </div>

      <TimeAwarenessBadge awareness={result.time_awareness ?? null} reason={result.time_awareness_reason ?? null} />

      <CoverageSection coverage={result.coverage ?? null} whatCouldChange={result.what_could_change ?? []} />

      {result.new_context_available && (
        <div className="card p-3 flex items-center justify-between gap-3 border-brand/40 bg-brand-soft/10">
          <p className="text-[11px] text-text leading-relaxed">Hay información nueva disponible desde este análisis.</p>
          <button onClick={onReanalyze} className="shrink-0 inline-flex items-center gap-1 text-[10px] font-black uppercase text-brand">
            <RefreshCw className="h-3 w-3" /> Reanalizar
          </button>
        </div>
      )}

      {result.probabilities_1x2 && (
        <div className="card p-4 space-y-2">
          <p className="eyebrow">Probabilidades principales</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-[10px] text-text-dim font-bold">LOCAL</p>
              <p className="figure text-lg font-black text-text">{formatProb(result.probabilities_1x2.home)}</p>
            </div>
            <div>
              <p className="text-[10px] text-text-dim font-bold">EMPATE</p>
              <p className="figure text-lg font-black text-text">{formatProb(result.probabilities_1x2.draw)}</p>
            </div>
            <div>
              <p className="text-[10px] text-text-dim font-bold">VISITANTE</p>
              <p className="figure text-lg font-black text-text">{formatProb(result.probabilities_1x2.away)}</p>
            </div>
          </div>
        </div>
      )}

      {result.status === 'no_value' && (
        <div className="card p-5 text-center space-y-2 border-border/60">
          <CircleSlash className="h-6 w-6 mx-auto text-text-dim" />
          <p className="text-sm font-bold text-text">Sin recomendación sólida</p>
          <p className="text-xs text-text-muted max-w-sm mx-auto">
            John Anthony analizó las familias elegidas, pero ninguna ofrece hoy una oportunidad
            suficientemente probable y sólida.
          </p>
        </div>
      )}

      {recommendations.length > 0 && (
        <div className="space-y-2">
          <p className="eyebrow text-brand">{recommendations.length === 1 ? 'Recomendación' : `${recommendations.length} recomendaciones`}</p>
          <div className="space-y-3">
            {recommendations.map((o, i) => (
              <OpportunityCard
                key={i} opp={o} highlight={i === 0} usageId={result.ja_lab_usage_id}
                matchLabel={`${fixture.home_team} vs ${fixture.away_team}`}
              />
            ))}
          </div>
        </div>
      )}

      <button onClick={onViewAllMarkets} className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-panel-elevated border border-border text-text-dim hover:text-text text-xs font-bold transition">
        <BarChart3 className="h-3.5 w-3.5" /> VER TODOS LOS MERCADOS
      </button>

      <div className="hairline pt-3 flex items-center justify-between text-[10px] text-text-dim">
        <span>{result.markets_analyzed_count} mercados evaluados{result.markets_excluded_count > 0 ? ` · ${result.markets_excluded_count} sin información suficiente` : ''}</span>
      </div>

      {dataNotes.map((note, i) => (
        <p key={i} className="text-[10px] text-text-dim/80 leading-relaxed">{note}</p>
      ))}

      <p className="text-[10px] text-text-dim/70 leading-relaxed">
        La cuota de mercado es una referencia capturada por John Anthony y puede haber cambiado
        desde ese momento. JA Lab nunca reemplaza el análisis de la cartera oficial diaria
        (Top IA / Top Seguro / Parlays) -- es una capacidad adicional para consultar partidos
        concretos bajo demanda.
      </p>
    </div>
  )
}

// ─── My Analyses History ───────────────────────────────────────────────────

function MyAnalyses({ items, onOpen, onLoadMore, hasMore }: {
  items: JALabHistoryItem[]
  onOpen: (item: JALabHistoryItem) => void
  onLoadMore: () => void
  hasMore: boolean
}) {
  const groups = useMemo(() => {
    const today = todayISO()
    const yesterday = addDays(today, -1)
    const buckets: { label: string; items: JALabHistoryItem[] }[] = [
      { label: 'Hoy', items: [] },
      { label: 'Ayer', items: [] },
      { label: 'Anteriores', items: [] },
    ]
    for (const item of items) {
      if (item.usage_date === today) buckets[0].items.push(item)
      else if (item.usage_date === yesterday) buckets[1].items.push(item)
      else buckets[2].items.push(item)
    }
    return buckets.filter((b) => b.items.length > 0).map((b) => [b.label, b.items] as const)
  }, [items])

  if (items.length === 0) return null

  return (
    <div id="ja-lab-history" className="space-y-4 pt-2 scroll-mt-4">
      <p className="eyebrow px-1 flex items-center gap-1.5"><HistoryIcon className="h-3 w-3" /> Mis análisis</p>
      {groups.map(([label, group]) => (
        <div key={label} className="space-y-1.5">
          <p className="text-[9px] font-black uppercase tracking-wider text-text-dim px-1">{label}</p>
          <div className="divide-y divide-border/30">
            {group.map((h) => (
              <button
                key={h.fixture_id}
                onClick={() => onOpen(h)}
                className="data-row w-full text-left hover:bg-panel-elevated/40 transition rounded-xl px-2 -mx-2"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex items-center -space-x-1.5 shrink-0">
                    <TeamCrest src={h.home_logo} alt={h.home_team ?? ''} size={20} />
                    <TeamCrest src={h.away_logo} alt={h.away_team ?? ''} size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-text truncate">{h.home_team ?? '—'} vs {h.away_team ?? '—'}</p>
                    <p className="text-[10px] text-text-dim truncate">{h.competition_name}{h.version > 1 ? ` · v${h.version}` : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {h.status === 'completed' && <CheckCircle2 className="h-3 w-3 text-success" />}
                  {h.status === 'no_value' && <CircleSlash className="h-3 w-3 text-text-dim" />}
                  <span className="text-[10px] text-text-dim">Ver análisis →</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
      {hasMore && (
        <button onClick={onLoadMore} className="w-full text-center text-[10px] font-black uppercase tracking-wider text-text-dim py-2">
          Cargar más
        </button>
      )}
    </div>
  )
}

// ─── Main Flow ─────────────────────────────────────────────────────────────

type FlowState =
  | 'home' | 'explore_countries' | 'explore_country' | 'competition_fixtures'
  | 'search' | 'detail' | 'loading' | 'result' | 'all_markets'

function BlackLabTool() {
  const { data: allowance } = useJALabAllowance(true)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<SearchFilter>('todos')
  // Sección 2 del encargo ("filtros combinables"): país/competición son filtros
  // estructurados adicionales al texto libre -- el backend ya los combina entre sí en
  // `search_fixtures` (`require_analyzable=True` siempre, Sección 1). `filter` (todos/
  // hoy/mañana) ahora se traduce a `date` real contra el backend en vez de un día local
  // calculado en el cliente -- una sola fuente de verdad de timezone (Sección 3).
  const [countryFilter, setCountryFilter] = useState<string>('')
  const [competitionFilter, setCompetitionFilter] = useState<number | null>(null)
  const [flow, setFlow] = useState<FlowState>('home')
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const [selectedCompetition, setSelectedCompetition] = useState<JALabCompetitionSummary | null>(null)
  const [selected, setSelected] = useState<FixtureContext | null>(null)
  const [selectedFamilies, setSelectedFamilies] = useState<JALabFamily[]>([])
  const [historyOffset, setHistoryOffset] = useState(0)
  const [historyItems, setHistoryItems] = useState<JALabHistoryItem[]>([])

  const { data: competitionsData, isFetching: loadingCompetitions } = useJALabCompetitions(
    flow === 'home' || flow === 'explore_countries' || flow === 'explore_country' || flow === 'search',
  )
  const { data: competitionFixturesData, isFetching: loadingCompetitionFixtures } = useJALabCompetitionFixtures(
    flow === 'competition_fixtures', selectedCompetition?.competition_id ?? null,
  )
  // Sección 3 del encargo ("corrige definitivamente los horarios"): `hoy`/`mañana` ya no se
  // calculan comparando `kickoff_utc` contra la hora local del navegador -- se piden al
  // backend como fecha real de calendario (mismo criterio de "día" que ya usa
  // `search_fixtures`, zona de negocio), evitando un segundo cálculo de timezone en el
  // cliente que pueda desincronizarse del real.
  const searchTargetDate = filter === 'hoy' ? todayISO() : filter === 'manana' ? addDays(todayISO(), 1) : undefined
  // P0 (07/10/2026): paginación explícita -- `limit` crece con "Ver más"; el backend devuelve
  // `total`/`has_more`, nunca un recorte silencioso.
  const [searchLimit, setSearchLimit] = useState(DISCOVERY_PAGE_SIZE)
  useEffect(() => {
    setSearchLimit(DISCOVERY_PAGE_SIZE)
  }, [query, filter, countryFilter, competitionFilter])
  const { data: searchData, isFetching: searching } = useJALabFixtureSearch(flow === 'search', query, {
    country: countryFilter || undefined,
    competitionId: competitionFilter ?? undefined,
    date: searchTargetDate,
    limit: searchLimit,
  })
  // Sección 1 del encargo: MISMO hook/endpoint que el buscador (query vacía = universo
  // analizable de hoy tal cual lo resuelve `search_fixtures` en el backend), solo para la
  // Home -- nunca un segundo criterio de disponibilidad.
  const { data: availableFixturesData, isFetching: loadingAvailableFixtures } = useJALabFixtureSearch(
    flow === 'home', '',
  )
  const { data: historyPage } = useJALabHistory(true, historyOffset)
  const { data: familiesData, isFetching: familiesLoading } = useJALabFamilies(flow === 'detail', selected?.fixture_id ?? null)
  const analyzeMutation = useJALabAnalyze()
  const reopenMutation = useJALabReopen()

  const allHistoryItems = useMemo(() => {
    if (historyOffset === 0) return historyPage?.items ?? []
    return [...historyItems, ...(historyPage?.items ?? [])]
  }, [historyPage, historyItems, historyOffset])

  const analyzedFixtureIds = useMemo(() => new Set(allHistoryItems.map((h) => h.fixture_id)), [allHistoryItems])

  // Sección 1 del encargo: fecha/hora primero (ya ordenado por el backend), competición como
  // desempate real para partidos al mismo horario -- nunca un criterio de disponibilidad nuevo.
  const availableFixtures = useMemo(() => {
    const fixtures = availableFixturesData?.fixtures ?? []
    return [...fixtures].sort((a, b) => {
      const byKickoff = a.kickoff_utc.localeCompare(b.kickoff_utc)
      if (byKickoff !== 0) return byKickoff
      return (a.competition_name ?? '').localeCompare(b.competition_name ?? '')
    })
  }, [availableFixturesData])

  // El día (hoy/mañana) ya se resuelve en el backend vía `date` (ver `searchTargetDate`
  // arriba) -- nunca un segundo filtro de "mismo día local" en el cliente.
  const filteredFixtures = searchData?.fixtures ?? []
  const searchTotal = searchData?.total ?? filteredFixtures.length
  const searchHasMore = searchData?.has_more ?? false

  // Universo real de países/competiciones para los filtros del buscador -- sale de la
  // MISMA lista que ya usa Explorar (`useJALabCompetitions`), nunca un segundo catálogo.
  const availableCountries = useMemo(() => {
    const countries = new Set<string>()
    for (const c of competitionsData?.competitions ?? []) {
      if (c.country) countries.add(c.country)
    }
    return Array.from(countries).sort((a, b) => a.localeCompare(b))
  }, [competitionsData])

  const availableCompetitionsForFilter = useMemo(() => {
    const all = competitionsData?.competitions ?? []
    if (!countryFilter) return all
    return all.filter((c) => c.country === countryFilter)
  }, [competitionsData, countryFilter])

  const activeResult = analyzeMutation.data ?? reopenMutation.data
  const activeError = analyzeMutation.error ?? reopenMutation.error

  function goHome() {
    setFlow('home')
    setSelectedCountry(null)
    setSelectedCompetition(null)
    setSelected(null)
    setSelectedFamilies([])
    setCountryFilter('')
    setCompetitionFilter(null)
    setFilter('todos')
    analyzeMutation.reset()
    reopenMutation.reset()
  }

  function goToSearch() {
    setFlow('search')
  }

  function goToExploreCountries() {
    setFlow('explore_countries')
  }

  function handleSelectCountry(groupLabel: string) {
    setSelectedCountry(groupLabel)
    setFlow('explore_country')
  }

  function handleSelectCompetition(c: JALabCompetitionSummary) {
    setSelectedCompetition(c)
    setFlow('competition_fixtures')
  }

  function handleSelectFromSearch(f: JALabFixtureSummary) {
    setSelected(fromSearch(f))
    setSelectedFamilies([])
    setFlow('detail')
  }

  function handleGoToHistory() {
    document.getElementById('ja-lab-history')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  function handleOpenFromHistory(h: JALabHistoryItem) {
    setSelected(fromHistory(h))
    setFlow('loading')
    reopenMutation.mutate({ fixtureId: h.fixture_id }, { onSuccess: () => setFlow('result'), onError: () => setFlow('result') })
  }

  function toggleFamily(family: JALabFamily) {
    setSelectedFamilies((prev) => {
      if (prev.includes(family)) return prev.filter((f) => f !== family)
      if (prev.length >= MAX_FAMILIES) return prev
      return [...prev, family]
    })
  }

  function handleConfirmAnalyze() {
    if (!selected || selectedFamilies.length === 0) return
    setFlow('loading')
    analyzeMutation.mutate(
      { fixtureId: selected.fixture_id, families: selectedFamilies },
      { onSuccess: () => setFlow('result'), onError: () => setFlow('result') },
    )
  }

  function handleReanalyzeFromResult() {
    if (!selected) return
    const usedFamilies = activeResult?.requested_families?.length ? activeResult.requested_families : selectedFamilies
    setSelectedFamilies(usedFamilies)
    setFlow('loading')
    analyzeMutation.mutate(
      { fixtureId: selected.fixture_id, families: usedFamilies },
      { onSuccess: () => setFlow('result'), onError: () => setFlow('result') },
    )
  }

  function handleViewExisting() {
    if (!selected) return
    setFlow('loading')
    reopenMutation.mutate({ fixtureId: selected.fixture_id }, { onSuccess: () => setFlow('result'), onError: () => setFlow('result') })
  }

  function loadMoreHistory() {
    setHistoryItems(allHistoryItems)
    setHistoryOffset((o) => o + 20)
  }

  return (
    <div className="space-y-5">
      {flow === 'home' && (
        <LabHome
          locked={false}
          competitions={competitionsData?.competitions ?? []}
          loadingCompetitions={loadingCompetitions}
          onSelectCompetition={handleSelectCompetition}
          onStartSearch={goToSearch}
          onExploreCountries={goToExploreCountries}
          onGoToHistory={handleGoToHistory}
          allowance={allowance}
          availableFixtures={availableFixtures}
          availableTotal={availableFixturesData?.total}
          loadingAvailableFixtures={loadingAvailableFixtures}
          analyzedFixtureIds={analyzedFixtureIds}
          onSelectFixture={handleSelectFromSearch}
        />
      )}

      {flow === 'home' && (
        <MyAnalyses
          items={allHistoryItems}
          onOpen={handleOpenFromHistory}
          onLoadMore={loadMoreHistory}
          hasMore={(historyPage?.items.length ?? 0) >= 20}
        />
      )}

      {flow === 'explore_countries' && (
        <ExploreCountriesView
          competitions={competitionsData?.competitions ?? []}
          loading={loadingCompetitions}
          onSelectCountry={handleSelectCountry}
          onBack={goHome}
        />
      )}

      {flow === 'explore_country' && selectedCountry && (
        <ExploreCountryView
          countryLabel={selectedCountry}
          competitions={competitionsData?.competitions ?? []}
          loading={loadingCompetitions}
          onSelectCompetition={handleSelectCompetition}
          onBack={() => setFlow('explore_countries')}
        />
      )}

      {flow === 'competition_fixtures' && selectedCompetition && (
        <CompetitionFixturesView
          competition={selectedCompetition}
          fixtures={competitionFixturesData?.fixtures ?? []}
          loading={loadingCompetitionFixtures}
          analyzedFixtureIds={analyzedFixtureIds}
          onSelect={handleSelectFromSearch}
          onBack={() => setFlow(selectedCountry ? 'explore_country' : 'home')}
        />
      )}

      {flow === 'search' && (
        <SearchView
          query={query}
          setQuery={setQuery}
          filter={filter}
          setFilter={setFilter}
          countryFilter={countryFilter}
          setCountryFilter={setCountryFilter}
          competitionFilter={competitionFilter}
          setCompetitionFilter={setCompetitionFilter}
          availableCountries={availableCountries}
          availableCompetitions={availableCompetitionsForFilter}
          filteredFixtures={filteredFixtures}
          total={searchTotal}
          hasMore={searchHasMore}
          onLoadMore={() => setSearchLimit((n) => n + DISCOVERY_PAGE_SIZE)}
          searching={searching}
          analyzedFixtureIds={analyzedFixtureIds}
          onSelect={handleSelectFromSearch}
          onBack={goHome}
        />
      )}

      {flow === 'detail' && selected && (
        <MatchDetailView
          fixture={selected}
          families={familiesData?.families ?? []}
          loadingFamilies={familiesLoading}
          selectedFamilies={selectedFamilies}
          onToggleFamily={toggleFamily}
          onAnalyze={handleConfirmAnalyze}
          alreadyAnalyzed={analyzedFixtureIds.has(selected.fixture_id)}
          remaining={allowance?.remaining ?? 0}
          unlimited={allowance?.unlimited ?? false}
          onViewExisting={handleViewExisting}
          onBack={() => {
            if (selectedCompetition) setFlow('competition_fixtures')
            else setFlow('search')
          }}
        />
      )}

      {flow === 'loading' && selected && <LoadingView fixture={selected} />}

      {flow === 'result' && selected && (
        <div className="space-y-3">
          {activeError !== null ? (
            <div className="space-y-4">
              <button onClick={() => selected && setFlow('detail')} className="text-[10px] font-black uppercase tracking-wider text-text-dim">
                ← Volver
              </button>
              <div className="card p-5 text-center space-y-2 border-danger/30">
                <AlertTriangle className="h-6 w-6 mx-auto text-danger" />
                <p className="text-sm font-bold text-text">{getJALabErrorMessage(activeError)}</p>
              </div>
            </div>
          ) : activeResult ? (
            <AnalysisResultView
              result={activeResult}
              fixture={selected}
              onReanalyze={handleReanalyzeFromResult}
              onBack={goHome}
              onViewAllMarkets={() => setFlow('all_markets')}
            />
          ) : null}
        </div>
      )}

      {flow === 'all_markets' && selected && activeResult && (
        <AllMarketsView
          fixture={selected}
          result={activeResult}
          onBack={() => setFlow('result')}
        />
      )}
    </div>
  )
}

// ─── Page Entry ────────────────────────────────────────────────────────────

export function JALabPage() {
  const { user } = useAuth()
  const { data } = useHome(true)
  const isBlack = Boolean(data?.plan_features?.personal_intelligence) || user?.role === 'ADMIN'
  // Sprint "JA Lab -- Cierre final discovery UX" (25/09/2026, Sección ADMIN del encargo): BUG
  // REAL confirmado -- este footer mostraba "Hasta 5 análisis por día" SIEMPRE, incluso para
  // ADMIN (`allowance.unlimited === true`, ya mostrado como "Análisis ilimitados" arriba en
  // `LabHome`/`BlackLabTool`) -- contradicción visual real. MISMO query (`useJALabAllowance`,
  // misma queryKey que ya usa `BlockLabTool`, React Query la deduplica, sin llamada extra).
  const { data: allowance } = useJALabAllowance(isBlack)

  return (
    <div className="p-4 pb-24 md:p-8 md:pb-8 space-y-8 md:max-w-4xl md:mx-auto">
      {isBlack ? (
        <BlackLabTool />
      ) : (
        <LabHome
          locked={true}
          competitions={[]}
          loadingCompetitions={false}
          onSelectCompetition={() => {}}
          onStartSearch={() => {}}
        />
      )}

      {!allowance?.unlimited && (
        <p className="text-[10px] text-text-dim/70 leading-relaxed max-w-xl flex items-center gap-1.5">
          <Clock className="h-3 w-3 shrink-0" />
          Hasta 5 análisis por día (Europe/Madrid, reinicia a medianoche). Reabrir un análisis ya
          hecho no consume tu cupo -- solo un reanálisis nuevo lo hace.
        </p>
      )}
    </div>
  )
}
